<template>
  <div>
    <AlertDismiss ref="alertDismiss" class="alert-sticky" />
    <b-overlay :show="isFetching">
      <b-form @submit.prevent="handleSubmit" class="row">
        <div class="mb-2">
          <label class="form-label mb-0" for="name"
            >Nombre de la plantilla</label
          >
          <input
            type="text"
            id="name"
            class="form-control"
            v-model="description"
            placeholder="Ingrese una descripción para la plantilla"
            required
          />
        </div>

        <MultiAutocomplete
          class="mb-1"
          v-model="recipients"
          @add="addRecipient"
          @remove="removeRecipient"
          :allowCustom="false"
          :items="mails"
          :placeholder="'Ingrese el email de los destinatarios (deben ser usuarios de la aplicación)'"
          invalidValueMsg="Ingrese un correo eléctronico válido"
          label="Destinatarios"
          inputType="email"
        />

        <div class="me-2 mb-2">
          <label>Roles destinatarios:</label>
          <select
            multiple
            class="form-control"
            v-model="selectedRoleIds"
            :size="selectSize"
          >
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.name }}
            </option>
          </select>
        </div>

        <div class="mb-3">
          <label for="fileInput">Documento</label>
          <input
            type="file"
            class="form-control"
            id="fileInput"
            ref="fileInput"
            @change="handleFileChange"
            required
            accept=".pdf,application/pdf"
          />
        </div>

        <div class="mb-3">
          <label class="form-label d-block">Preguntas de la plantilla</label>
          <b-button
            variant="outline-secondary"
            @click="openQuestionModal"
            class="mt-1"
          >
            <b-icon icon="pencil-square" class="me-1"></b-icon> Editar Preguntas
            ({{ modelValue.length }})
          </b-button>
        </div>

        <BaseModal
          v-model="showQuestionModal"
          title="Editar Preguntas de la Plantilla"
          size="lg"
          hide-footer
        >
          <StatementQuestionModal
            :initial-questions="modelValue"
            @update-questions="handleUpdateQuestions"
            @cancel="closeQuestionModal"
          />
        </BaseModal>

        <div class="mt-2 row">
          <div class="col-auto">
            <b-button
              type="submit"
              variant="primary"
              :disabled="isUploading || modelValue.length === 0"
              v-b-tooltip.hover
              :title="
                modelValue.length === 0
                  ? 'Debe agregar al menos una pregunta'
                  : ''
              "
            >
              <b-spinner small v-if="isUploading"></b-spinner>
              Crear
            </b-button>
          </div>
          <div class="col">
            <ProgressCustom ref="progress" />
          </div>
        </div>
      </b-form>
    </b-overlay>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import BaseModal from "@/components/shared/Base-modal.vue";
import StatementQuestionModal from "@/components/PepStatements/PepStatementMenu/PepStatementSetting/PepStatementTemplateForm/Pep-statement-question-modal.vue";
import ProgressCustom from "@/components/shared/Progress-custom.vue";
import AlertDismiss from "@/components/shared/Alert-dismiss.vue";
import { EventNames } from "@/plugins/enums";
import MultiAutocomplete from "@/components/shared/Autocomplete-input.vue";

export default {
  name: "StatementTemplateForm",

  components: {
    ProgressCustom,
    AlertDismiss,
    MultiAutocomplete,
    BaseModal,
    StatementQuestionModal,
  },

  data() {
    return {
      description: "",
      route: null,
      recipients: [],
      modelValue: [],
      selectedRoleIds: [],
      showQuestionModal: false,

      file: null,
      mails: [],
      roles: [],
      isUploading: false,
      isFetching: false,
    };
  },

  computed: {
    selectSize() {
      return this.roles.length;
    },
  },

  async mounted() {
    this.isFetching = true;
    this.modelValue = [];
    await this.getMails();
    await this.getRoles();
    this.isFetching = false;
  },

  methods: {
    ...mapActions([
      "uploadPepDocumentFile",
      "savePepStatementTemplate",
      "getAllMails",
      "allRoles",
    ]),

    async handleSubmit() {
      this.isUploading = true;

      this.$refs.progress.progress(15, "Subiendo documento...");
      const uploadedDocument = await this.uploadDocument();
      if (!uploadedDocument) {
        this.isUploading = false;
        this.$refs.progress.progress(0);
        return;
      }

      this.$refs.progress.progress(70, "Creando plantilla...");

      const templateResult = await this.uploadTemplate();
      if (!templateResult) {
        this.$refs.progress.progress(0);
        this.isUploading = false;
        return;
      }

      this.$refs.progress.progress(100, "Proceso completado");
      this.$refs.alertDismiss.showAlert(
        "Plantilla creada con éxito",
        "success"
      );
      this.$eventBus.$emit(EventNames.CreatedStatementTemplate);
      setTimeout(() => {
        this.$refs.progress.progress(0);
      }, 1000);

      this.clearForm();
    },

    async getMails() {
      const result = await this.getAllMails();

      if (result.isError) {
        this.$refs.alertDismiss.showAlert(result.message, "warning");
        return;
      }

      this.mails = result.data.map((x) => {
        return {
          id: x.id,
          name: x.mail,
        };
      });
    },

    async getRoles() {
      const result = await this.allRoles();

      if (result.isError) {
        this.$refs.alertDismiss.showAlert(result.message, "warning");
        return;
      }

      this.roles = result.data;
    },

    async uploadTemplate() {
      const questions = this.modelValue.map((x) => {
        return {
          name: x.name,
          fieldNameCargo: x.fieldNameCargo,
          fieldNameDate: x.fieldNameDate,
          childrenQuestions: x.subquestions.map((subq) => {
            return {
              name: subq.name,
              fieldNameCargo: subq.fieldNameCargo,
              fieldNameDate: subq.fieldNameDate,
            };
          }),
        };
      });

      const result = await this.savePepStatementTemplate({
        name: this.description,
        questions: questions,
        documentRoute: this.route,
        addresseeIds: this.recipients.map((x) => x.id),
        addresseeRoleIds: this.selectedRoleIds,
      });

      if (result.isError) {
        this.$refs.alertDismiss.showAlert(result.message, "warning");
        this.isUploading = false;
        return false;
      }
      return true;
    },

    async uploadDocument() {
      // if we have uploaded the document, we don't need to upload it again
      if (this.route) {
        return true;
      }
      const result = await this.uploadPepDocumentFile(this.file);
      if (result.isError) {
        this.$refs.alertDismiss.showAlert(result.message, "warning");
        this.isUploading = false;
        return false;
      }

      this.route = result.data;
      return true;
    },

    clearForm() {
      this.isUploading = false;
      this.description = "";
      this.modelValue = []; // Reset questions
      this.route = null;
      this.file = null;
      // Check if fileInput ref exists before accessing value
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = null;
      }
      this.recipients = [];
      this.selectedRoleIds = [];
    },

    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.file = file;
      }
    },

    openQuestionModal() {
      this.showQuestionModal = true;
    },

    closeQuestionModal() {
      this.showQuestionModal = false;
    },

    handleUpdateQuestions(updatedQuestions) {
      this.modelValue = updatedQuestions;
      this.closeQuestionModal();
    },

    addRecipient(recipient) {
      this.recipients.push(recipient);
    },

    removeRecipient(recipient) {
      this.recipients.splice(this.recipients.indexOf(recipient), 1);
    },
  },

  watch: {
    file() {
      this.route = null;
    },
  },
};
</script>

<style scoped>
.alert-sticky {
  position: sticky;
  top: 0;
  z-index: 100;
}
</style>
